import _ from 'lodash';
import Vue from 'vue';
import moment from 'moment';
import reactiveCashflow from 'reactive-cashflow';
import cashflowHistory from '../utilities/cashflow-history';
import cashflowNormalizer from '../utilities/cashflow-normalizer';
import cashflowViewConsts from '../../constants/cashflow-view';
import CashflowViewApi from '../../api/CashflowViewApi';

const ECONOMY_DEFAULT_NAME = 'סופר';

const state = {
  presentedBudgetDate: null,
  allCashflows: [],
  cashflowUITree: {},
  cashflow: {},
  actionsExpandedFor: null,
  finishLoadingAllBudgets: false,
  lastActivity: null,
};

function transactionsInCurrentCashflow(state) {
  const { envelopes, excluded, variableTransactions, variableIncomeTransactions } = state.cashflow;
  return _.chain(envelopes).flatMap(envelope => envelope.actuals).concat(excluded).concat(variableTransactions)
    .concat(variableIncomeTransactions)
    .value();
}

function normalizeTrackingCategories(trackingCategories) {
  return _.map(trackingCategories, category => {
    return {
      categoryId: category.categoryId,
      categoryName: cashflowViewConsts.CATEGORY_NAMES.TRACKING,
      expense: category.categoryName,
      cashflowCategory: cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING,
      icon: category.icon,
      label: category.categoryName,
      pluralLabel: category.categoryName,
      style: cashflowViewConsts.CATEGORY_STYLE[cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING],
    };
  });
}

const getters = {
  allCategories: (state, getters) => [...cashflowViewConsts.CATEGORIES, ...normalizeTrackingCategories(getters.trackingCategories)],
  currentBudgetDate: () => cashflowHistory.getCurrentBudgetDate(),
  presentedBudget: state => _.find(state.allCashflows, { budgetDate: state.presentedBudgetDate }),
  allBudgetDates: state => _getAllBudgetDates(state),
  lastBudgetDate: state => _.last(_getAllBudgetDates(state)),
  predictedBalanceAtEndOfMonth: state => -1 * state.cashflow.predictedBalance,
  remainingVariables: state => state.cashflow.variableBalancedAmount - state.cashflow.variableActualSum,
  riseupGoal: (state, getters) => (getters.remainingVariables < 0 ? state.cashflow.riseupGoal + getters.remainingVariables
    : state.cashflow.riseupGoal),
  budgetUpdatedAt: state => state.cashflow.lastUpdatedAt,
  totalIncome: state => state.cashflow.variableIncomeSum + state.cashflow.incomeEnvelopesActualSum,
  totalExpense: state => state.cashflow.variableActualSum + state.cashflow.expenseEnvelopesActualSum,
  monthName: state => moment(state.presentedBudgetDate).locale('he').format('MMM'),
  monthFullName: state => moment(state.presentedBudgetDate).locale('he').format('MMMM'),
  balance: state => -1 * state.cashflow.balance,
  cashflowGraphHistory: (state, getters) => _.chain(state.allCashflows)
    .thru(cashflows => cashflowHistory.getCashflowsForPastMonthsGraph(cashflows, state.presentedBudgetDate, getters.currentBudgetDate))
    .map(budget => {
      const cashflow = reactiveCashflow.createCashflow(budget);
      return {
        cashflowDate: budget.budgetDate,
        total: -1 * cashflow.balance,
      };
    })
    .value(),
  supermarketGraphHistory: (state, getters) => _.chain(state.allCashflows)
    .reject(cashflow => cashflow.budgetDate === getters.currentBudgetDate)
    .sortBy(cashflow => cashflow.budgetDate)
    .slice(-5)
    .reverse()
    .map(budget => {
      const cashflow = cashflowNormalizer.normalizeCashflow(reactiveCashflow.createCashflow(budget));
      return {
        budgetDate: budget.budgetDate,
        total: _.find(cashflow.trackingCategory, c => c.categoryName === ECONOMY_DEFAULT_NAME)?.upToNowAmount || 0,
      };
    })
    .value(),
  excluded: state => state.cashflow.excluded,
  hasExcluded: state => cashflowNormalizer.visibleExcluded(state.cashflow.excluded).length > 0,
  isPresentingCurrentMonth: (state, getters) => state.presentedBudgetDate === getters.currentBudgetDate,
  isPresentingLastMonth: (state, getters) => state.presentedBudgetDate === getters.lastBudgetDate,
  trackingCategories: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.trackingCategory],
  oneTimeSavingCategory: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.oneTimeSaving][0],
  weeks: state => state.cashflowUITree[cashflowViewConsts.cashflowTypes.variableExpense].weekly.realExpenses.categories,
  currentWeek: (state, getters) => _.find(getters.weeks, w => w.timeFrameDetails.isCurrentWeek),
  remainingWeeklyVariables: (state, getters) => getters.currentWeek.remaining,
  hasNextWeek: (state, getters) => getters.currentWeek.weekIndex < getters.weeks.length - 1,
  nextWeekRemainingVariables: (state, getters) => (getters.hasNextWeek ? getters.weeks[getters.currentWeek.weekIndex + 1].remaining : 0),
  fixedExpenseCategories: state => state.cashflow.fixedTree.fixedExpenseEnvelopes.map(e => e.details.expense),
  fixedIncomeCategories: state => state.cashflow.fixedTree.fixedIncomeEnvelopes.map(e => e.details.expense),
  transactionsInCurrentCashflow,
  transactionsSinceLastActivity: (state, getters, _, rootGetters) => {
    if (!state.lastActivity) {
      return [];
    }
    let transactionsDiffStartDate = moment(state.lastActivity);
    const updatedTransactionMaxAge = moment().subtract(rootGetters['featureFlags/cashflowUpdateIndicatorsAge'], 'days');
    if (updatedTransactionMaxAge.isAfter(state.lastActivity)) {
      // we should not show transactions older than the max age, if they happened after the last activity
      transactionsDiffStartDate = updatedTransactionMaxAge;
    }
    return getters.transactionsInCurrentCashflow
      .filter(t => moment(t.createdAt).isAfter(transactionsDiffStartDate))
      .filter(t => !t.excluded)
      .map(t => t.transactionId);
  },
};

const actions = {
  async initBudgets({ commit, dispatch }, { budgetDate }) {
    const budgetToDisplayFirst = await cashflowHistory.getBudget(budgetDate);
    commit('setCashflows', [budgetToDisplayFirst]);
    await dispatch('updatePresentedBudgetDate', budgetDate);

    dispatch('loadAllBudgets');
  },
  async loadAllBudgets({ commit, dispatch, getters }) {
    const budgets = await CashflowViewApi.fetchAllBudgets();
    // This loads also FUTURE budgets which we have no use for, so we throw them out
    commit('setCashflows', _.reject(budgets, ({ budgetDate }) => (budgetDate > getters.currentBudgetDate)));

    if (!getters.presentedBudget) {
      const latestBudgetDate = _.max(getters.allBudgetDates);
      await dispatch('updatePresentedBudgetDate', latestBudgetDate);
    }
    commit('setFinishLoadingAllBudgets');
  },
  async updatePresentedBudgetDate({ commit, dispatch, getters }, budgetDate) {
    commit('setPresentedBudgetDate', budgetDate);
    await dispatch('resetBudgetTree');
  },
  async resetBudgetTree({ commit, getters }) {
    const budget = getters.presentedBudget;
    if (budget) {
      const cashflow = reactiveCashflow.createCashflow(budget);
      const cashflowUITree = cashflowNormalizer.normalizeCashflow(cashflow);
      commit('setCashflow', cashflow);
      commit('setCashflowUITree', cashflowUITree);
    }
  },
  async moveToNextBudgetMonth({ dispatch }) {
    const nextBudgetDate = cashflowHistory.getNextBudgetDate(state.presentedBudgetDate);
    await dispatch('updatePresentedBudgetDate', nextBudgetDate);
  },
  async moveToPrevBudgetMonth({ dispatch }) {
    const nextBudgetDate = cashflowHistory.getPrevBudgetDate(state.presentedBudgetDate);
    await dispatch('updatePresentedBudgetDate', nextBudgetDate);
  },
  expandActionsOf({ commit }, id) {
    commit('expandActionsOf', id);
  },
};

function _getAllBudgetDates(state) {
  return state.allCashflows.map(({ budgetDate }) => budgetDate);
}

// mutations
const mutations = {
  setCashflowOptimistic(state, newCashflow) {
    const index = _.findIndex(state.allCashflows, { budgetDate: state.presentedBudgetDate });
    // use Vue.set to update arrays and induce reactivity
    Vue.set(state.allCashflows, index, newCashflow);
  },
  setCashflows(state, allCashflows) {
    state.allCashflows = allCashflows;
  },
  setCashflow(state, cashflow) {
    state.cashflow = cashflow; //eslint-disable-line
  },
  setCashflowUITree(state, cashflowUITree) {
    state.cashflowUITree = cashflowUITree;
  },
  setPresentedBudgetDate(state, budgetDate) {
    state.presentedBudgetDate = budgetDate;
  },
  expandActionsOf(state, id) {
    state.actionsExpandedFor = id;
  },
  setFinishLoadingAllBudgets(state) {
    state.finishLoadingAllBudgets = true;
  },
  setLastActivity(state, lastActivity) {
    state.lastActivity = lastActivity;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
